import Pagination from "@dana/MobiusOverrides/Pagination/Pagination";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface EndYokeResultScreenProps {
    assembly: string;
    make: string;
    model: string;
    pageNo: number;
    fields?: any;
}

interface FindEndYokeResultStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    rowWrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    twoColResultWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    diagramImage?: ImgProps;
    productImage?: ImgProps;
    productInfoWrapper?: InjectableCss;
    productPartNumbersWrapper?: InjectableCss;
    productPartNumbersStyles?: TypographyPresentationProps;
    productPartNumbersNoLinkStyles?: TypographyPresentationProps;
    productDescriptionStyles?: TypographyPresentationProps;
    totalResultText?: TypographyPresentationProps;
    attributeTable?: DataTablePresentationProps;
    attributeTableCell?: DataTableCellProps;
    searchButton?: ButtonPresentationProps;
}

export const findEndYokeResultStyles: FindEndYokeResultStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            padding: 16px 16px 16px 16px;
        `,
    },
    contentWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            padding: 15px 40px 100px 50px;
            height: 430px;
            display: flex;
            flex-direction: column;
            align-items: left;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    twoColWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            & > * {
                flex: 1 1 0;
                min-width: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },

    twoColResultWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            cursor: pointer;
            & > *:first-child {
                flex: 0 0 30%;
                max-width: 30%;
            }
            & > *:last-child {
                flex: 0 0 70%;
                max-width: 70%;
            }

            &:focus,
            &:focus-visible {
                outline-color: ${themeColors.secondary.danaDarkBlue};
                outline-style: solid;
                outline-width: 1px;
                outline-offset: 2px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            & > * {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                            & > *:first-child {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                            & > *:last-child {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    heading: {
        variant: "h3",
    },
    diagramImage: {
        css: css`
            width: 100%;
            height: auto;
            max-height: 350px;
            object-fit: contain;
        `,
    },
    productImage: {
        css: css`
            width: 50px;
            min-width: 50px;
            max-width: 50px;
            height: auto;
            object-fit: contain;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            max-width: 100%;
                            display: block;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    totalResultText: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeTable: {
        css: css`
            width: 100%;
            border-collapse: collapse;
            border: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    attributeTableCell: {
        css: css`
            word-break: break-word;
            white-space: normal;
        `,
    },
    searchButton: {
        variant: "primary",
    },
    productPartNumbersWrapper: {
        css: css`
            a {
                text-decoration: none;
            }
        `,
    },
    productPartNumbersStyles: {
        css: css`
            color: ${themeColors.primary.danaBlue};
            text-decoration: none;
            transition: text-decoration 0.2s;
            &:hover {
                text-decoration: underline;
            }
        `,
        variant: "h4",
    },
    productPartNumbersNoLinkStyles: {
        css: css`
            color: ${themeBackgrounds.darkGray};
        `,
        variant: "h4",
    },
    productDescriptionStyles: {
        css: css`
            color: ${themeColors.primary.richBlack};
        `,
    },
    rowWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
            box-sizing: border-box;
        `,
    },

    productInfoWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            padding: 15px 40px 100px 50px;
            flex: 0 0 calc(50% - 8px);
            max-width: calc(50% - 8px);
            box-sizing: border-box;
            display: flex;
            flex-direction: row;
            gap: 16px;

            & > * {
                flex: 1 1 0;
                min-width: 0;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 0 0 100%;
                            max-width: 100%;
                            padding: 15px 10px 100px 10px;
                            flex-direction: column;
                            align-items: stretch;
                            gap: 15px;
                            & > * {
                                width: 100%;
                                min-width: 0;
                            }
                        `,
                        css`
                            flex: 0 0 100%;
                            max-width: 100%;
                            padding: 15px 20px 100px 20px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
};

type EndYokeFlange = {
    erpNumber: string;
    partName: string;
    partImage: string;
    partUrl: string;
    position: string;
    series: string;
};

const defaultAssemblyData: string[] = [
    "AUXILIARY TRANSMISSIONS",
    "SINGLE AXLES",
    "SPLIT SHAFT PTO",
    "TANDEM AXLES",
    "TRANSFER CASES",
    "TRANSMISSIONS",
];
const getAssemblyImageKey = (assembly: string) => {
    switch (assembly) {
        case "AUXILIARY TRANSMISSIONS":
            return ["auxiliaryTransmissionsImage", "auxiliaryTransmissionsImageAltText"];
        case "SINGLE AXLES":
            return ["singleAxlesImage", "singleAxlesImageAltText"];
        case "SPLIT SHAFT PTO":
            return ["splitShaftPtoImage", "splitShaftPtoImageAltText"];
        case "TANDEM AXLES":
            return ["tandemAxlesImage", "tandemAxlesImageAltText"];
        case "TRANSFER CASES":
            return ["transferCasesImage", "transferCasesImageAltText"];
        case "TRANSMISSIONS":
            return ["transmissionsImage", "transmissionsImageAltText"];
        default:
            return [undefined, undefined];
    }
};

const EndYokeResultScreen: React.FC<EndYokeResultScreenProps> = ({ assembly, make, model, pageNo, fields }) => {
    const styles = findEndYokeResultStyles;
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [selectedAssembly, setSelectedAssembly] = useState(assembly);
    const [selectedMake, setSelectedMake] = useState(make);
    const [selectedModel, setSelectedModel] = useState(model);
    const [assemblyData, setAssemblyData] = useState<string[]>(defaultAssemblyData);
    const [makesData, setMakesData] = useState<string[]>([]);
    const [modelsData, setModelsData] = useState<string[]>([]);
    const totalResultMessage = translate("Showing {0} results for End Yoke/Flange");
    const [searchResultMessage, setSearchResultMessage] = useState("");
    const [results, setResults] = useState<EndYokeFlange[]>([]);
    const [totalResult, setTotalResult] = useState(0);
    const [imageKey, altKey] = getAssemblyImageKey(selectedAssembly);
    const imageUrl = imageKey ? fields[imageKey] : "";
    const imageAltText = altKey ? fields[altKey] : "";

    const fetchMakes = async (assembly: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findEndYoke/makes?assembly=${assembly}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setMakesData(data);
        } catch (error) {
            console.error("Error fetching makes:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const fetchModels = async (assembly: string, make: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findEndYoke/models?assembly=${assembly}&make=${make}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setModelsData(data);
        } catch (error) {
            console.error("Error fetching models:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const search = () => {
        if (!selectedAssembly || !selectedMake || !selectedModel) return;

        const params = new URLSearchParams({
            page_no: "1",
            assemblyObj: selectedAssembly,
            make: selectedMake,
            model: selectedModel,
        });
        window.location.search = `?${params.toString()}`;
    };

    const handleAssemblyChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedMake("");
        setSelectedModel("");
        setSelectedAssembly(selectedValue);
        fetchMakes(selectedValue);
    };

    const handleManufacturerChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedModel("");
        setSelectedMake(selectedValue);
        fetchModels(selectedAssembly, selectedValue);
    };

    const handleModelChange: SelectProps["onChange"] = event => {
        setSelectedModel(event.target.value);
    };

    const changePage = (newPageIndex: number) => {
        if (newPageIndex < 1) {
            newPageIndex = 1;
        }
        const params = new URLSearchParams({
            page_no: newPageIndex.toString(),
            assemblyObj: selectedAssembly,
            make: selectedMake,
            model: selectedModel,
        });
        window.location.search = `?${params.toString()}`;
    };

    const fetchResult = async () => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(
                `/api/v1/findEndYoke/search?page=${pageNo}&assembly=${selectedAssembly}&make=${selectedMake}&model=${selectedModel}`,
            );
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setTotalResult(data.total);
            setSearchResultMessage(totalResultMessage.replace("{0}", data.total.toString()));
            setResults(data.results ?? []);
        } catch (error) {
            console.error("Error fetching filters:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const handleOpenPartUrl = (partUrl?: string) => {
        if (!partUrl) return;
        window.open(partUrl, "_blank", "noreferrer");
    };

    useEffect(() => {
        fetchMakes(selectedAssembly);
        fetchModels(selectedAssembly, selectedMake);
        fetchResult();
    }, []);

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <StyledWrapper {...styles.twoColWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Find End Yoke/Flange")}
                        </Typography>
                        <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                            <Select
                                label={translate("Assembly")}
                                name="assembly"
                                onChange={handleAssemblyChange}
                                value={selectedAssembly}
                            >
                                <option value="">{translate("Select Assembly")}</option>
                                {assemblyData &&
                                    assemblyData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <Select
                                label={translate("Manufacturer")}
                                name="manufacturer"
                                onChange={handleManufacturerChange}
                                disabled={!selectedAssembly}
                                value={selectedMake}
                            >
                                <option value="">{translate("Select Manufacturer")}</option>
                                {makesData &&
                                    makesData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <Select
                                label={translate("Model")}
                                name="model"
                                onChange={handleModelChange}
                                disabled={!selectedMake || !selectedAssembly}
                                value={selectedModel}
                            >
                                <option value="">{translate("Select Model")}</option>
                                {modelsData &&
                                    modelsData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <Button
                                {...styles.searchButton}
                                onClick={search}
                                disabled={!selectedAssembly || !selectedMake || !selectedModel}
                            >
                                {translate("GO")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Assembly Diagram")}
                        </Typography>
                        <Img {...styles.diagramImage} src={imageUrl} altText={imageAltText || ""} />
                    </StyledWrapper>
                </StyledWrapper>

                <Typography {...styles.totalResultText} as="h4">
                    {searchResultMessage}
                </Typography>

                <StyledWrapper {...styles.rowWrapper}>
                    {results.map((result, index) => (
                        <StyledWrapper key={index} {...styles.productInfoWrapper}>
                            <StyledWrapper
                                {...styles.twoColResultWrapper}
                                onClick={() => handleOpenPartUrl(result?.partUrl)}
                                tabIndex={0}
                                role="button"
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenPartUrl(result?.partUrl);
                                    }
                                }}
                            >
                                <StyledWrapper>
                                    <Img {...styles.productImage} src={result.partImage} altText={result.partName} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.productPartNumbersWrapper}>
                                    {result.partUrl ? (
                                        <Typography {...styles.productPartNumbersStyles}>{result.erpNumber}</Typography>
                                    ) : (
                                        <Typography {...styles.productPartNumbersNoLinkStyles}>
                                            {result.erpNumber}
                                        </Typography>
                                    )}
                                    <Typography {...styles.productDescriptionStyles} as="span">
                                        {result.partName}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <DataTable {...styles.attributeTable}>
                                <DataTableBody>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Position")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>{result.position}</DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Series")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>{result.series}</DataTableCell>
                                    </DataTableRow>
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>

                {totalResult > 0 && (
                    <Pagination
                        resultsCount={totalResult}
                        currentPage={pageNo}
                        resultsPerPage={10}
                        resultsPerPageOptions={[]}
                        onChangePage={changePage}
                        onChangeResultsPerPage={() => {}}
                        pageSizeCookie="FindEndYoke-PageSize"
                        data-test-selector="FindEndYoke_pagination"
                    />
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default EndYokeResultScreen;
